// ════════════════════════════════════════════════════════════════════
// RECON REPORT VIEW — AI Accounting / Reconciliation Agent (PDF Prompt 2).
//
// Three sections:
//   1. "Sync to DB" — sends in-app transactions to /api/recon/mirror-transactions
//      so the server-side agent has Postgres data to query.
//   2. Chat box — posts to /api/recon/agent with a question; renders the
//      prose-table answer (with full agent names + variance breakdowns).
//   3. Recent runs list — read-only history of recon_runs the agent has produced.
//
// The agent uses the deterministic engine (api/recon/run) under the
// hood and never re-derives net amounts.
// ════════════════════════════════════════════════════════════════════

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body && body.error) || (typeof body === 'string' ? body.slice(0, 200) : `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
}

const QUICK_QUESTIONS = [
  'Reconcile MOO for last month.',
  'Reconcile Americo for last month.',
  'Reconcile Transamerica for last month.',
  'What\'s our persistency this month?',
  'Show me underpayments across all carriers.',
];

export default function ReconReportView({ currentUser, showToast = () => {} }) {
  const [question, setQuestion] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [answer, setAnswer]   = React.useState('');
  const [meta, setMeta]       = React.useState(null);   // { run_id, carrier, period_month, raw }
  const [error, setError]     = React.useState(null);

  const syncToDb = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      let transactions = [];
      let deposits = [];
      try {
        const raw = localStorage.getItem(`crm:state:${currentUser?.id || ''}`);
        const st  = raw ? JSON.parse(raw) : null;
        transactions = Array.isArray(st?.transactions) ? st.transactions : [];
        deposits     = Array.isArray(st?.deposits)     ? st.deposits     : [];
      } catch { /* ignore */ }
      if (transactions.length === 0) {
        showToast('No transactions in app state — upload a statement first.');
        setSyncing(false);
        return;
      }
      const json = await apiFetch('/api/recon/mirror-transactions', {
        method: 'POST',
        body:   JSON.stringify({ transactions, deposits }),
      });
      showToast(`Synced ${json.inserted_transactions} transactions (${json.skipped_transactions} duplicates skipped) + ${json.inserted_deposits} deposit rows.`);
    } catch (err) {
      showToast(`Sync failed: ${err.message || err}`);
    } finally {
      setSyncing(false);
    }
  };

  const ask = async (qOverride) => {
    const q = (qOverride || question || '').trim();
    if (!q || busy) return;
    setBusy(true);
    setError(null);
    try {
      const json = await apiFetch('/api/recon/agent', {
        method: 'POST',
        body:   JSON.stringify({ question: q }),
      });
      setAnswer(json.answer || '(no answer)');
      setMeta(json);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-xl p-5">
        <div className="text-amber-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Reconciliation Agent</div>
        <h1 className="text-xl font-bold mt-1">AI Accounting · Carrier Reconciliation</h1>
        <p className="text-slate-300 text-sm mt-2">
          Reconciles parsed commission activity against actual EFT deposits per carrier. MOO net = Comm Amt.
          Transamerica net = Adv Comm. Americo authoritative on FF-prefix rows with EFT as only real cash.
          Owner / overrides / agent buckets stay separate. Persistency is a policy-count ratio.
        </p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            onClick={syncToDb}
            disabled={syncing}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-bold text-sm px-4 py-2 rounded-md"
          >
            {syncing ? 'Syncing…' : 'Sync transactions to DB'}
          </button>
          <div className="text-xs text-slate-400">Run once after uploading new statements to mirror them into Postgres.</div>
        </div>
      </div>

      {/* Quick-question chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {QUICK_QUESTIONS.map(q => (
          <button
            key={q}
            onClick={() => { setQuestion(q); ask(q); }}
            disabled={busy}
            className="text-xs text-slate-700 bg-white border border-slate-200 hover:border-slate-400 disabled:opacity-50 rounded-md px-2.5 py-1.5"
          >{q}</button>
        ))}
      </div>

      {/* Ask box */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles size={14} className="text-amber-500"/>
          <span className="text-sm font-semibold text-slate-900">Ask the reconciliation agent</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') ask(); }}
            placeholder="e.g. Reconcile MOO for 2026-05 — break out by writing agent"
            className="flex-1 text-sm border border-slate-300 rounded-md px-3 py-2"
          />
          <button
            onClick={() => ask()}
            disabled={busy || !question.trim()}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-md"
          >
            {busy ? 'Working…' : 'Ask'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800">
          {error}
        </div>
      )}

      {answer && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          {meta && (
            <div className="flex items-center gap-2 flex-wrap mb-3 text-xs">
              {meta.carrier && <span className="font-bold uppercase tracking-wider text-[10px] bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded">{meta.carrier}</span>}
              {meta.period_month && <span className="font-bold uppercase tracking-wider text-[10px] bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded">{meta.period_month}</span>}
              {meta.raw && (
                <>
                  <span className="text-slate-500">Expected: <span className="font-bold tabular-nums text-slate-900">${(meta.raw.expected || 0).toLocaleString()}</span></span>
                  <span className="text-slate-500">Actual: <span className="font-bold tabular-nums text-slate-900">${(meta.raw.actual || 0).toLocaleString()}</span></span>
                  <span className={`font-bold tabular-nums ${meta.raw.variance >= 0 ? 'text-amber-700' : 'text-rose-700'}`}>
                    Variance: ${(meta.raw.variance || 0).toLocaleString()}
                  </span>
                </>
              )}
            </div>
          )}
          <div className="text-sm text-slate-900 whitespace-pre-wrap">{answer}</div>
        </div>
      )}
    </div>
  );
}
